import { createClient } from '@supabase/supabase-js';

// Use process.env for server-side (Trigger.dev) and import.meta.env for client-side
const supabaseUrl = typeof process !== 'undefined' && process.env?.VITE_SUPABASE_URL 
  ? process.env.VITE_SUPABASE_URL 
  : import.meta.env?.VITE_SUPABASE_URL;

const supabaseAnonKey = typeof process !== 'undefined' && process.env?.VITE_SUPABASE_ANON_KEY 
  ? process.env.VITE_SUPABASE_ANON_KEY 
  : import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Detect user's timezone from browser
 * @returns {string} IANA timezone string
 */
export function detectUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Failed to detect timezone, defaulting to EST:', error);
    return 'America/New_York';
  }
}

/**
 * Detect user's location using browser geolocation API
 * @returns {Promise<Object>} Location object with coordinates and timezone
 */
export async function detectUserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocoding to get city/state
          const locationData = await reverseGeocode(latitude, longitude);
          
          resolve({
            coordinates: { lat: latitude, lng: longitude },
            timezone: detectUserTimezone(),
            ...locationData
          });
        } catch (error) {
          console.error('Error processing location data:', error);
          reject(error);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
}

/**
 * Reverse geocoding to get city/state from coordinates
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object>} Location details
 */
async function reverseGeocode(lat, lng) {
  try {
    // Using a free reverse geocoding service
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
    );
    
    if (!response.ok) {
      throw new Error('Reverse geocoding failed');
    }
    
    const data = await response.json();
    
    return {
      city: data.city || data.locality || 'Unknown',
      state: data.principalSubdivision || data.administrativeArea || 'Unknown',
      country: data.countryCode || 'US',
      countryName: data.countryName || 'United States'
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    // Fallback to basic location detection
    return {
      city: 'Unknown',
      state: 'Unknown',
      country: 'US',
      countryName: 'United States'
    };
  }
}

/**
 * Get common US timezones for dropdown
 * @returns {Array} Array of timezone options
 */
export function getCommonTimezones() {
  return [
    { value: 'America/New_York', label: 'Eastern Time (ET)', offset: '-05:00' },
    { value: 'America/Chicago', label: 'Central Time (CT)', offset: '-06:00' },
    { value: 'America/Denver', label: 'Mountain Time (MT)', offset: '-07:00' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: '-08:00' },
    { value: 'America/Phoenix', label: 'Arizona Time (MST)', offset: '-07:00' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKST)', offset: '-09:00' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)', offset: '-10:00' }
  ];
}

/**
 * Get US states for dropdown
 * @returns {Array} Array of state options
 */
export function getUSStates() {
  return [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];
}

/**
 * Normalize time string to HH:MM format (handles HH:MM:SS or HH:MM)
 * @param {string} timeStr - Time string in HH:MM or HH:MM:SS format
 * @returns {string} Time in HH:MM format
 */
export function normalizeTimeFormat(timeStr) {
  if (!timeStr) return timeStr;
  // Remove seconds if present (HH:MM:SS -> HH:MM)
  return timeStr.length > 5 ? timeStr.substring(0, 5) : timeStr;
}

/**
 * Check if current time is within agent's SMS schedule
 * @param {string} timezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM or HH:MM:SS format)
 * @param {string} endTime - End time (HH:MM or HH:MM:SS format)
 * @param {number[]} allowedDays - Array of allowed days (1=Monday, 7=Sunday)
 * @returns {boolean} Whether it's okay to send SMS now
 */
/**
 * Normalize schedule days to storage format (1=Mon … 7=Sun).
 * Legacy UI saved Sunday as 0 instead of 7.
 */
export function normalizeScheduleDays(allowedDays = [1, 2, 3, 4, 5]) {
  return allowedDays.map((d) => (d === 0 ? 7 : d));
}

export function isWithinSMSSchedule(timezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5]) {
  try {
    const now = new Date();
    const agentTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));

    const normalizedAllowedDays = normalizeScheduleDays(allowedDays);

    // Check if current day is allowed
    const currentDay = agentTime.getDay() || 7; // Convert Sunday (0) to 7
    if (!normalizedAllowedDays.includes(currentDay)) {
      return false;
    }
    
    // Normalize times to HH:MM format
    const normalizedStartTime = normalizeTimeFormat(startTime);
    const normalizedEndTime = normalizeTimeFormat(endTime);
    
    // Check if current time is within schedule
    const currentTime = agentTime.toTimeString().substring(0, 5); // HH:MM format
    return currentTime >= normalizedStartTime && currentTime <= normalizedEndTime;
  } catch (error) {
    console.error('Error checking SMS schedule:', error);
    return false; // Default to not sending if there's an error
  }
}

/**
 * Get next available send time for an agent
 * @param {string} timezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {Date} Next available send time
 */
export function getNextAvailableSendTime(timezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5]) {
  try {
    const now = new Date();
    const agentTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    
    // If we're within schedule today, return now
    if (isWithinSMSSchedule(timezone, startTime, endTime, allowedDays)) {
      return now;
    }
    
    // Find next available time
    let nextTime = new Date(agentTime);
    nextTime.setHours(parseInt(startTime.split(':')[0]), parseInt(startTime.split(':')[1]), 0, 0);
    
    // If it's past end time today, move to next day
    const currentTime = agentTime.toTimeString().substring(0, 5);
    if (currentTime > endTime) {
      nextTime.setDate(nextTime.getDate() + 1);
    }
    
    const normalizedAllowedDays = normalizeScheduleDays(allowedDays);

    // Find next allowed day
    while (!normalizedAllowedDays.includes(nextTime.getDay() || 7)) {
      nextTime.setDate(nextTime.getDate() + 1);
    }
    
    // Convert back to UTC
    const utcOffset = agentTime.getTimezoneOffset();
    return new Date(nextTime.getTime() + (utcOffset * 60000));
  } catch (error) {
    console.error('Error calculating next send time:', error);
    return new Date(Date.now() + 24 * 60 * 60 * 1000); // Default to tomorrow
  }
}

/**
 * Update agent's location and auto-detect timezone
 * @param {string} agentId - Agent's UUID
 * @param {Object} locationData - Location information
 * @returns {Promise<Object>} Updated profile with detected timezone
 */
export async function updateAgentLocation(agentId, locationData) {
  try {
    const { data, error } = await supabase.rpc('update_user_location', {
      user_id: agentId,
      city: locationData.city,
      state: locationData.state,
      country: locationData.country || 'US',
      lat: locationData.coordinates?.lat || null,
      lng: locationData.coordinates?.lng || null
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating agent location:', error);
    throw error;
  }
}

/**
 * Get current time for an agent in their timezone
 * @param {string} timezone - Agent's timezone
 * @returns {Object} Current time information
 */
export function getAgentCurrentTime(timezone) {
  try {
    const now = new Date();
    const agentTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const timeString = agentTime.toTimeString().substring(0, 5); // HH:MM format
    const hour = agentTime.getHours();
    const isBusinessHours = hour >= 9 && hour < 17;
    
    return {
      timeString,
      timezone,
      dateTime: agentTime,
      isBusinessHours,
      hour,
      minute: agentTime.getMinutes()
    };
  } catch (error) {
    console.error('Error getting agent current time:', error);
    const now = new Date();
    return {
      timeString: now.toTimeString().substring(0, 5),
      timezone: 'UTC',
      dateTime: now,
      isBusinessHours: false,
      hour: now.getHours(),
      minute: now.getMinutes()
    };
  }
}

/**
 * Update agent's timezone and SMS schedule
 * @param {string} agentId - Agent's UUID
 * @param {Object} scheduleData - Schedule configuration
 * @returns {Promise<Object>} Updated profile
 */
export async function updateAgentSchedule(agentId, scheduleData) {
  try {
    // Normalize times to HH:MM format before saving
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        timezone: scheduleData.timezone,
        sms_schedule_start: normalizeTimeFormat(scheduleData.startTime),
        sms_schedule_end: normalizeTimeFormat(scheduleData.endTime),
        sms_schedule_days: scheduleData.allowedDays,
        sms_schedule_enabled: scheduleData.enabled,
        location_detection_enabled: scheduleData.locationDetectionEnabled,
        updated_at: new Date().toISOString()
      })
      .eq('id', agentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating agent schedule:', error);
    throw error;
  }
}

/**
 * Get agent's current location and schedule info
 * @param {string} agentId - Agent's UUID
 * @returns {Promise<Object>} Agent's location and schedule data
 */
export async function getAgentLocationSchedule(agentId) {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        id,
        full_name,
        timezone,
        location_city,
        location_state,
        location_country,
        location_coordinates,
        sms_schedule_start,
        sms_schedule_end,
        sms_schedule_days,
        sms_schedule_enabled,
        location_detection_enabled
      `)
      .eq('id', agentId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching agent location/schedule:', error);
    throw error;
  }
}

/**
 * Format location for display
 * @param {Object} locationData - Location data from database
 * @returns {string} Formatted location string
 */
export function formatLocation(locationData) {
  if (!locationData) return 'Location not set';
  
  const parts = [];
  if (locationData.location_city) parts.push(locationData.location_city);
  if (locationData.location_state) parts.push(locationData.location_state);
  if (locationData.location_country && locationData.location_country !== 'US') {
    parts.push(locationData.location_country);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Location not set';
}

/**
 * Get optimal time slots for SMS sending based on engagement research
 * @param {string} timezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {Array} Array of optimal time slots
 */
export function getOptimalTimeSlots(timezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5]) {
  // Normalize times to HH:MM format
  const normalizedStartTime = normalizeTimeFormat(startTime);
  const normalizedEndTime = normalizeTimeFormat(endTime);
  
  // Research-based optimal SMS sending times
  const optimalTimes = [
    { start: '09:00', end: '10:30', weight: 0.9, description: 'Morning peak' },
    { start: '11:00', end: '12:00', weight: 0.8, description: 'Late morning' },
    { start: '14:00', end: '15:30', weight: 0.95, description: 'Afternoon peak' },
    { start: '16:00', end: '17:00', weight: 0.85, description: 'End of day' }
  ];

  // Filter optimal times that fall within agent's schedule
  const availableSlots = optimalTimes.filter(slot => {
    return slot.start >= normalizedStartTime && slot.end <= normalizedEndTime;
  });

  // If no optimal slots fit, create reasonable defaults
  if (availableSlots.length === 0) {
    const startHour = parseInt(normalizedStartTime.split(':')[0]);
    const endHour = parseInt(normalizedEndTime.split(':')[0]);
    const duration = endHour - startHour;
    
    if (duration >= 4) {
      // Split into morning and afternoon slots
      return [
        { 
          start: normalizedStartTime, 
          end: `${startHour + Math.floor(duration/2)}:00`, 
          weight: 0.7, 
          description: 'Morning slot' 
        },
        { 
          start: `${startHour + Math.floor(duration/2)}:00`, 
          end: normalizedEndTime, 
          weight: 0.7, 
          description: 'Afternoon slot' 
        }
      ];
    } else {
      // Single slot
      return [{
        start: normalizedStartTime,
        end: normalizedEndTime,
        weight: 0.6,
        description: 'Business hours'
      }];
    }
  }

  return availableSlots;
}

/**
 * Generate intelligent send times within optimal slots
 * @param {Array} clients - Array of clients
 * @param {string} timezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {Array} Clients with optimized send times
 */
export function generateOptimalSendTimes(clients, timezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5]) {
  // Normalize times to HH:MM format
  const normalizedStartTime = normalizeTimeFormat(startTime);
  const normalizedEndTime = normalizeTimeFormat(endTime);
  const optimalSlots = getOptimalTimeSlots(timezone, normalizedStartTime, normalizedEndTime, allowedDays);
  const scheduledClients = [];
  
  // Distribute clients across optimal time slots
  const clientsPerSlot = Math.ceil(clients.length / optimalSlots.length);
  
  let clientIndex = 0;
  
  for (let slotIndex = 0; slotIndex < optimalSlots.length && clientIndex < clients.length; slotIndex++) {
    const slot = optimalSlots[slotIndex];
    const slotClients = clients.slice(clientIndex, clientIndex + clientsPerSlot);
    
    // Generate random times within this slot for each client
    for (let i = 0; i < slotClients.length; i++) {
      const client = slotClients[i];
      const sendTime = generateRandomTimeInSlot(slot, timezone, allowedDays);
      
      scheduledClients.push({
        ...client,
        scheduledSendTime: sendTime,
        sendOrder: clientIndex + i + 1,
        timeSlot: slot.description,
        slotWeight: slot.weight
      });
    }
    
    clientIndex += slotClients.length;
  }
  
  // Sort by scheduled send time
  return scheduledClients.sort((a, b) => a.scheduledSendTime - b.scheduledSendTime);
}

/**
 * Generate a random time within a specific time slot
 * @param {Object} slot - Time slot object
 * @param {string} timezone - Agent's timezone
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {Date} Random time within the slot
 */
function generateRandomTimeInSlot(slot, timezone, allowedDays) {
  const now = new Date();
  const agentTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  
  // Parse slot times
  const slotStart = slot.start.split(':');
  const slotEnd = slot.end.split(':');
  
  const startMinutes = parseInt(slotStart[0]) * 60 + parseInt(slotStart[1]);
  const endMinutes = parseInt(slotEnd[0]) * 60 + parseInt(slotEnd[1]);
  
  // Generate random minutes within the slot
  const randomMinutes = startMinutes + Math.floor(Math.random() * (endMinutes - startMinutes));
  
  // Create time for today
  let sendTime = new Date(agentTime);
  sendTime.setHours(Math.floor(randomMinutes / 60), randomMinutes % 60, 0, 0);
  
  // If the time has passed today, move to next available day
  if (sendTime <= agentTime) {
    sendTime.setDate(sendTime.getDate() + 1);
  }
  
  // Find next allowed day
  while (!allowedDays.includes(sendTime.getDay() || 7)) {
    sendTime.setDate(sendTime.getDate() + 1);
  }
  
  // Convert back to UTC
  const utcOffset = agentTime.getTimezoneOffset();
  return new Date(sendTime.getTime() + (utcOffset * 60000));
}

/**
 * Check if a specific time is within optimal sending windows
 * @param {string} timeString - Time in HH:MM format
 * @param {string} timezone - Agent's timezone
 * @returns {Object} Optimal time analysis
 */
export function analyzeSendTime(timeString, timezone) {
  const hour = parseInt(timeString.split(':')[0]);
  const minute = parseInt(timeString.split(':')[1]);
  const totalMinutes = hour * 60 + minute;
  
  // Define optimal time windows based on SMS engagement research
  const optimalWindows = [
    { start: 540, end: 630, score: 0.9, description: 'Morning peak (9:00-10:30)' },
    { start: 660, end: 720, score: 0.8, description: 'Late morning (11:00-12:00)' },
    { start: 840, end: 930, score: 0.95, description: 'Afternoon peak (2:00-3:30)' },
    { start: 960, end: 1020, score: 0.85, description: 'End of day (4:00-5:00)' }
  ];
  
  // Check if time falls within any optimal window
  const window = optimalWindows.find(w => totalMinutes >= w.start && totalMinutes <= w.end);
  
  if (window) {
    return {
      isOptimal: true,
      score: window.score,
      description: window.description,
      recommendation: 'Excellent time for SMS engagement'
    };
  }
  
  // Check if it's a reasonable time (not too early/late)
  if (totalMinutes >= 480 && totalMinutes <= 1080) { // 8:00 AM - 6:00 PM
    return {
      isOptimal: false,
      score: 0.6,
      description: 'Business hours',
      recommendation: 'Good time, but not optimal'
    };
  }
  
  // Outside business hours
  return {
    isOptimal: false,
    score: 0.3,
    description: 'Outside business hours',
    recommendation: 'Consider scheduling during business hours for better engagement'
  };
}

/**
 * Get recommended time slots for an agent based on their schedule
 * @param {string} timezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {Object} Recommendations with optimal times
 */
export function getSendTimeRecommendations(timezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5]) {
  // Normalize times to HH:MM format
  const normalizedStartTime = normalizeTimeFormat(startTime);
  const normalizedEndTime = normalizeTimeFormat(endTime);
  const optimalSlots = getOptimalTimeSlots(timezone, normalizedStartTime, normalizedEndTime, allowedDays);
  const currentTime = getAgentCurrentTime(timezone);
  
  return {
    timezone,
    currentTime: currentTime.timeString,
    isBusinessHours: currentTime.isBusinessHours,
    optimalSlots,
    recommendations: [
      'Send messages during morning peak (9:00-10:30) for highest engagement',
      'Afternoon peak (2:00-3:30) is also excellent for SMS response rates',
      'Avoid sending during lunch hours (12:00-1:00)',
      'End-of-day messages (4:00-5:00) work well for follow-ups',
      'Space messages 15-30 minutes apart to avoid overwhelming clients'
    ],
    nextOptimalTime: getNextOptimalSendTime(timezone, normalizedStartTime, normalizedEndTime, allowedDays)
  };
}

/**
 * Get the next optimal send time for an agent
 * @param {string} timezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {Date} Next optimal send time
 */
function getNextOptimalSendTime(timezone, startTime, endTime, allowedDays) {
  // Normalize times to HH:MM format
  const normalizedStartTime = normalizeTimeFormat(startTime);
  const normalizedEndTime = normalizeTimeFormat(endTime);
  const optimalSlots = getOptimalTimeSlots(timezone, normalizedStartTime, normalizedEndTime, allowedDays);
  const now = new Date();
  const agentTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  const currentTime = agentTime.toTimeString().substring(0, 5);
  
  // Find the next optimal slot
  for (const slot of optimalSlots) {
    if (currentTime < slot.start) {
      // Slot is later today
      const sendTime = new Date(agentTime);
      const [hours, minutes] = slot.start.split(':').map(Number);
      sendTime.setHours(hours, minutes, 0, 0);
      
      // Check if it's an allowed day
      if (allowedDays.includes(sendTime.getDay() || 7)) {
        const utcOffset = agentTime.getTimezoneOffset();
        return new Date(sendTime.getTime() + (utcOffset * 60000));
      }
    }
  }
  
  // No optimal slot today, find next available day
  let nextDay = new Date(agentTime);
  nextDay.setDate(nextDay.getDate() + 1);
  
  while (!allowedDays.includes(nextDay.getDay() || 7)) {
    nextDay.setDate(nextDay.getDate() + 1);
  }
  
  // Use first optimal slot of the next available day
  const firstSlot = optimalSlots[0];
  const [hours, minutes] = firstSlot.start.split(':').map(Number);
  nextDay.setHours(hours, minutes, 0, 0);
  
  const utcOffset = agentTime.getTimezoneOffset();
  return new Date(nextDay.getTime() + (utcOffset * 60000));
}

/**
 * Get timezone offset for display
 * @param {string} timezone - IANA timezone
 * @returns {string} Formatted offset (e.g., "UTC-5")
 */
export function getTimezoneOffset(timezone) {
  try {
    const now = new Date();
    const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    const local = new Date(utc.toLocaleString('en-US', { timeZone: timezone }));
    const offset = (local.getTime() - utc.getTime()) / (1000 * 60 * 60);
    
    return offset >= 0 ? `UTC+${offset}` : `UTC${offset}`;
  } catch (error) {
    return 'UTC-5'; // Default to EST
  }
}
